<script lang="ts" setup>
  import { DateTime } from 'luxon'
  import { useI18n } from 'vue-i18n'
  import WidgetSkeleton from '@/components/dashboard/WidgetSkeleton.vue'
  import { useFeatureFlags } from '@/composables/useFeatureFlag.ts'
  import { useGeneratedServiceRevenueStore } from '@/stores'
  import { ServiceRevenueDefaultReadSync } from '@/api'

  const isLoading = ref<boolean>(true)
  const titrop = useTitrop()
  const featureFlag = useFeatureFlags()
  const { t } = useI18n()

  const revenueStore = useGeneratedServiceRevenueStore()
  const lastRevenueEntry = ref<ServiceRevenueDefaultReadSync>()

  onMounted(async () => {
    lastRevenueEntry.value = await revenueStore.findAll({
      query: {
        itemsPerPage: 1,
        'order[refDateTime]': 'desc',
        'refDateTime[after]': DateTime.now().startOf('day').toISO({ suppressMilliseconds: true })
      }
    }, { forceRefresh: true }).then(d => d.value?.[0])
    isLoading.value = false
  })
</script>

<template>
  <div class="max-w-full @container/WidgetYearRevenue container-inline">
    <div v-if="titrop.user.roles.department_leader && featureFlag.isReady && featureFlag.isEnabled('widget_year_revenue')">
      <WidgetSkeleton v-if="isLoading" variant="year-revenue" />
      <div v-else
           class="w-full">
        <div class="w-full space-y-2">
          <div class="flex flex-col @sm:flex-row items-center @sm:justify-between mb-2">
            <div class="flex items-baseline gap-1">
              <p class="font-medium">{{ t('current') }}</p>
              <p class="text-lg sm:text-lg font-semibold text-gray-900">{{ lastRevenueEntry?.totalRevenue?.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) }} EUR</p>
            </div>
            <p class="text-sm text-gray-400">{{ t('perMonth', { value: ((lastRevenueEntry?.totalRevenue ?? 0) / DateTime.fromISO(lastRevenueEntry?.refDateTime!).month)?.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) }) }}</p>
          </div>

          <div class="flex flex-col @sm:flex-row items-center @sm:justify-between">
            <div class="flex items-baseline gap-1">
              <p class="font-medium">{{ DateTime.fromISO(lastRevenueEntry?.refDateTime!).minus({ year: 1 }).year }}:</p>
              <p class="text-lg sm:text-lg font-semibold text-gray-900">{{ lastRevenueEntry?.prevYearTotalRevenue?.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) }} EUR</p>
            </div>
            <p class="text-sm text-gray-400">{{ t('perMonth', { value: ((lastRevenueEntry?.prevYearTotalRevenue ?? 0) / 12)?.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) }) }}</p>
          </div>
        </div>
        <div class="text-xs text-gray-500 mt-1 border-t border-gray-100 pt-1">
          {{ t('asOf', { value: $formatter.DateTime(lastRevenueEntry?.refDateTime!) }) }}
        </div>
      </div>
    </div>
    <div v-else class="w-full">
      <Message>
        <span v-if="!featureFlag.isEnabled('widget_year_revenue')">{{ t('disabled') }}</span>
        <span v-else-if="!titrop.user.roles.department_leader">{{ t('leaderOnly') }}</span>
      </Message>
    </div>
  </div>
</template>

<i18n>
{
  "de": {
    "current": "Aktuell:",
    "target": "Ziel: {value}",
    "hours": "{value} Std.",
    "perMonth": "ø {value} EUR/Monat",
    "asOf": "Stand: {value}",
    "disabled": "Dieses Widget ist vorübergehend deaktiviert.",
    "leaderOnly": "Der Inhalt ist nur für Benutzer mit der Rolle \"Abteilungsleiter\" verfügbar."
  },
  "en": {
    "current": "Current:",
    "target": "Target: {value}",
    "hours": "{value} hrs",
    "perMonth": "Avg. {value} EUR/month",
    "asOf": "As of: {value}",
    "disabled": "This widget is temporarily disabled.",
    "leaderOnly": "This content is only available to users with the department leader role."
  }
}
</i18n>
